import { FunctionComponent } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { Col, Row } from 'react-styled-flexboxgrid';

import { INPUT_TYPES } from '@savgroup-front-common/constants';
import { FormGroup, Input } from '../../../../atoms/Form';
import { safeFormattedIntlString } from '../../../../formatters';
import { RangeNumericRubric } from '../../Datatable.types';
import { DatatableFiltersModel } from '../DatatableFiltersModel';

import { useFilterRange } from './FiltersRange.hooks';
import FiltersRangeTag from './FiltersRangeTag';
import messages from './messages';

const FiltersRange: FunctionComponent<{
  filter: RangeNumericRubric;
  onChange: (selectedFilter: RangeNumericRubric) => void;
  dataTestId?: string;
}> = ({ filter, onChange, dataTestId }) => {
  const {
    control,
    formState: { errors },
  } = useFormContext();

  const {
    defaultMin,
    defaultMax,
    min,
    max,
    onChangeRangeMin,
    onChangeRangeMax,
  } = useFilterRange({ filter, onChange });

  return (
    <DatatableFiltersModel
      title={filter.label}
      tag={
        <FiltersRangeTag
          min={min}
          max={max}
          defaultMin={defaultMin}
          defaultMax={defaultMax}
          dataTestId={`${dataTestId}_filtersRange_tag_${filter.name}`}
        />
      }
      filterContent={
        <FormGroup>
          <Row>
            <Col xs={6}>
              <Controller
                name={`filterStartRangeNumeric_${filter.name}`}
                control={control}
                render={({ field }) => (
                  <Input
                    ref={field.ref}
                    value={min === undefined ? '' : min}
                    name={field.name}
                    type={INPUT_TYPES.NUMBER}
                    errors={errors}
                    placeholder={`${safeFormattedIntlString(
                      messages.minValue,
                    )} (${defaultMin})`}
                    max={max !== undefined ? max : defaultMax}
                    min={defaultMin > 0 ? 0 : defaultMin}
                    onChange={(event) => {
                      const newValue = parseFloat(event.target.value);

                      field.onChange(
                        Number.isNaN(newValue) ? undefined : newValue,
                      );
                      onChangeRangeMin();
                    }}
                    onBlur={(event) => {
                      const newValue = parseFloat(event.target.value);

                      field.onChange(
                        Number.isNaN(newValue) ? undefined : newValue,
                      );
                    }}
                    dataTestId={`${dataTestId}_filterStartRangeNumeric_${filter.name}`}
                  />
                )}
              />
            </Col>
            <Col xs={6}>
              <Controller
                name={`filterEndRangeNumeric_${filter.name}`}
                control={control}
                render={({ field }) => (
                  <Input
                    ref={field.ref}
                    value={max === undefined ? '' : max}
                    name={field.name}
                    type={INPUT_TYPES.NUMBER}
                    errors={errors}
                    disableBrowserAutocomplete
                    placeholder={`${safeFormattedIntlString(
                      messages.maxValue,
                    )} (${defaultMax})`}
                    max={defaultMax}
                    min={min !== undefined ? min : defaultMin}
                    onChange={(event) => {
                      const newValue = parseFloat(event.target.value);

                      field.onChange(
                        Number.isNaN(newValue) ? undefined : newValue,
                      );
                      onChangeRangeMax();
                    }}
                    onBlur={(event) => {
                      const newValue = parseFloat(event.target.value);

                      field.onChange(
                        Number.isNaN(newValue) ? undefined : newValue,
                      );
                    }}
                    dataTestId={`${dataTestId}_filterEndRangeNumeric_${filter.name}`}
                  />
                )}
              />
            </Col>
          </Row>
        </FormGroup>
      }
    />
  );
};

FiltersRange.displayName = 'FiltersRange';
export default FiltersRange;
